import { usePathname } from 'next/navigation';

import { useLocalStorage } from '@mantine/hooks';

import { AppName, UserReferrer } from 'models';
import { AppHelpers } from 'helpers/AppHelpers';
import { AuthenticationHelpers } from 'helpers/AuthenticationHelpers';
import { UserHelpers } from 'helpers/UserHelpers';

import { useSnapshot } from 'hooks/use-safe-snapshot';
import { useWorkspaceConfig } from 'hooks/use-workspace-config';
import { currentAppState } from 'state/current-app';
import { state as currentUserState } from 'state/current-user';

export const useCurrentApp = (): AppName => {
    const pathname = usePathname() ?? '';
    const { workspace } = useWorkspaceConfig();
    const { lastActiveApp } = useSnapshot(currentAppState);
    const { user } = useSnapshot(currentUserState);
    const defaultApp = useDefaultApp();

    return AppHelpers.resolve({
        pathname,
        workspace: !!workspace,
        lastActiveApp:
            lastActiveApp?.userId === undefined || lastActiveApp.userId === (user?.id ?? null)
                ? lastActiveApp?.app
                : undefined,
        defaultApp,
        loginRedirect: AuthenticationHelpers.loginRedirectPath(pathname),
    });
};

export const useDefaultApp = (): AppName | undefined => {
    const { user } = useSnapshot(currentUserState);
    const [storedReferrer] = useLocalStorage<UserReferrer | undefined>({
        key: UserHelpers.localStorageKey.referrer,
        getInitialValueInEffect: false,
    });

    if (user) {
        return AppHelpers.getAppForReferrer(user.referrer);
    }

    return storedReferrer ? AppHelpers.getAppForReferrer(storedReferrer) : undefined;
};
